import Header from '@shared/components/Header/Header';
import cx from 'classnames';
import { PropsWithChildren } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import CreateNewButton from 'components/CreateNewButton';
import HeaderLogo from 'components/HeaderLogo/HeaderLogo';
import LanguageSelect from 'components/LanguageSelect/LanguageSelect';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import NavigationProvider from 'modules/Messenger/components/Sidebar/NavigationContext/NavigationContext';
import { FeatureFlag } from 'utils/Constants';

import AuthenticatedHeaderContent from './AuthenticatedHeaderContent';
import HelpDropdown from './HelpDropdown/HelpDropdown';

interface PageWrapperProps {
  isFullPageScroll?: boolean;
}

const PageWrapper = ({ children, isFullPageScroll = true }: PropsWithChildren<PageWrapperProps>) => {
  const { pathname } = useLocation();
  const isMessengerEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_MESSENGER);
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { mxUserId, mxAccessToken, mxDeviceId } = useMxCookies();

  const isAuthPage = pathname === '/auth';
  const hasMatrixClient = matrixClient !== null && Boolean(mxUserId && mxAccessToken && mxDeviceId);
  const shouldShowAuthenticatedContent = hasMatrixClient && !isAuthPage;
  const hasActiveRoom = !!matchPath('/messenger/:roomId', pathname);

  return (
    <NavigationProvider>
      <div
        className={cx(
          'top-area-padding side-area-padding flex w-full flex-col overflow-x-hidden',
          isFullPageScroll ? 'h-full overflow-y-auto' : 'h-[100dvh] min-h-0 overflow-hidden'
        )}
      >
        <div className={cx('shrink-0', { 'hidden lg:block': isMessengerEnabled && hasActiveRoom })}>
          <Header variant="light">
            <HeaderLogo className="ml-10" variant="primary" />
            <Header.Right className="!gap-2">
              {shouldShowAuthenticatedContent && (
                <div className="md:mr-4">
                  <CreateNewButton />
                </div>
              )}
              <LanguageSelect wrapperClassName="hidden md:flex" />
              {shouldShowAuthenticatedContent ? <AuthenticatedHeaderContent /> : <HelpDropdown />}
            </Header.Right>
          </Header>
        </div>
        {isFullPageScroll ? (
          children
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">{children}</div>
        )}
      </div>
    </NavigationProvider>
  );
};

export default PageWrapper;
